import React, { memo, useCallback, useEffect, useState } from "react";
import {
  Modal,
  View,
  TextInput,
  Pressable,
  TouchableWithoutFeedback,
  ActivityIndicator,
} from "react-native";
import RNPickerSelect from "react-native-picker-select";
import { IReportCallData } from "../../../types/call";
import StyledText from "../../common/ui/styled-text";
import { useFormattedDate } from "../../../hooks/useFormattedDate";
import { Check, ChevronDown, Flag, X } from "lucide-react-native";

type ReportCallIssueModalProps = {
  visible: boolean;
  onClose: () => void;
  onSubmit: (data: IReportCallData) => Promise<void>;
  callId?: string | null;
  callTimestamp?: string | null;
};

const placeholderTopic = "Select the type of issue...";
const otherTopic = "Other";
const reportReasons = [
  placeholderTopic,
  "Ordering - Order taken incorrectly",
  "Ordering - Order sent in wrong",
  "Ordering - Incorrect pricing given",
  "Ordering - Order allowed an unallowable thing",
  "Reservation - Reservation issue",
  "Technical - Technical issue",
  otherTopic,
];

const MAX_DESCRIPTION_LENGTH = 3000;

const ReportCallModal = ({
  visible,
  onClose,
  onSubmit,
  callId,
  callTimestamp,
}: ReportCallIssueModalProps) => {
  const [topic, setTopic] = useState<string>(placeholderTopic);
  const [description, setDescription] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | undefined>(undefined);
  const [isSuccess, setIsSuccess] = useState(false);
  const { formatDate, formatTime } = useFormattedDate();
  const [localCallId, setLocalCallId] = useState<string | undefined>(undefined);

  useEffect(() => {
    if (callId != localCallId && callId) {
      resetForm();
      setLocalCallId(callId);
    }
  }, [callId, visible]);

  const resetForm = useCallback(() => {
    setTopic(placeholderTopic);
    setDescription("");
    setError(undefined);
    setIsSuccess(false);
  }, []);

  const handleSubmit = useCallback(async () => {
    if (topic === placeholderTopic) return;

    setIsSubmitting(true);
    setError(undefined);
    try {
      await onSubmit({ topic, description: description.trim() });
      setIsSuccess(true);
    } catch (error) {
      setError("There was an error submitting your report. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  }, [onSubmit, topic, description]);

  const handleClose = useCallback(() => {
    if (isSuccess) {
      resetForm();
    }
    onClose();
  }, [isSuccess, onClose, resetForm]);

  const isOtherTopic = topic === otherTopic;

  const isSubmitDisabled =
    topic === placeholderTopic ||
    (isOtherTopic && description.trim().length === 0);

  return (
    <Modal
      visible={visible}
      transparent
      animationType="none"
      onRequestClose={handleClose}
    >
      <TouchableWithoutFeedback onPress={handleClose}>
        <View className="flex-1 justify-center items-center bg-black/50">
          <TouchableWithoutFeedback>
            <View className="w-[90%] max-w-md bg-white rounded-2xl p-6 max-sm:overflow-y-auto">
              <Pressable
                onPress={handleClose}
                className="p-1 absolute top-3 right-3 z-50"
                hitSlop={10}
              >
                <X size={20} color="#4c4c4c" />
              </Pressable>

              {isSuccess ? (
                <View className="items-center py-8">
                  <View className="bg-green-100 p-4 rounded-full mb-4">
                    <Check size={32} color="#22c55e" />
                  </View>
                  <StyledText className="text-xl font-semibold mb-2">
                    Report Submitted
                  </StyledText>
                  <StyledText className="text-gray-500 text-center">
                    Your feedback has been sent to our team and will be reviewed
                    shortly.
                  </StyledText>
                </View>
              ) : (
                <>
                  {/* Header */}
                  <View className="mb-5">
                    <View className="flex-row items-center gap-2 mb-2">
                      <Flag size={20} />
                      <StyledText className="text-xl font-semibold">
                        Report a call issue
                      </StyledText>
                    </View>
                    <StyledText className="text-gray-500 text-sm">
                      Submit a report about problems encountered during this
                      call
                      {callId &&
                        callTimestamp &&
                        ` on ${formatDate(callTimestamp)} at ${formatTime(
                          callTimestamp
                        )}.`}
                    </StyledText>
                  </View>

                  {/* Form */}
                  <View className="gap-y-4">
                    {/* Category Select */}
                    <View className="gap-y-2">
                      <StyledText className="font-medium">
                        Topic{" "}
                        <StyledText className="text-red-500">*</StyledText>
                      </StyledText>
                      <RNPickerSelect
                        items={reportReasons.map((reason) => ({
                          label: reason,
                          value: reason,
                        }))}
                        value={topic}
                        onValueChange={(value) => setTopic(value)}
                        placeholder={{}}
                        style={{
                          placeholder: {
                            color: "#4b5563",
                            fontSize: 14,
                            paddingHorizontal: 16,
                          },
                          iconContainer: {
                            top: 12,
                            right: 12,
                          },
                          inputWeb: {
                            borderRadius: 6,
                            borderWidth: 1,
                            borderColor: "#9ca3af",
                            paddingHorizontal: 16,
                            paddingVertical: 8,
                            backgroundColor: "#ffffff",
                            fontSize: 14,
                          },
                        }}
                        Icon={() => {
                          return <ChevronDown size={16} color="#000000" />;
                        }}
                      />
                    </View>

                    {/* Description Input */}
                    <View className="gap-y-2">
                      <View className="flex-row justify-between items-center">
                        <View className="flex-row items-center">
                          <StyledText className="font-medium">
                            Details{" "}
                          </StyledText>
                          {isOtherTopic ? (
                            <StyledText className="text-red-500">*</StyledText>
                          ) : (
                            <StyledText className="text-xs text-gray-500 font-light ml-[2px]">
                              (optional)
                            </StyledText>
                          )}
                        </View>
                      </View>
                      <TextInput
                        className="text-[16px] border border-gray-200 rounded-lg p-4 h-32 focus-within:outline-gray-700"
                        multiline
                        textAlignVertical="top"
                        placeholder="Please provide some details about the issue..."
                        placeholderTextColor="#9ca3af"
                        value={description}
                        onChangeText={setDescription}
                        maxLength={MAX_DESCRIPTION_LENGTH}
                      />
                    </View>
                  </View>

                  {error && (
                    <StyledText className="text-red-500 text-sm mt-3">
                      {error}
                    </StyledText>
                  )}

                  <StyledText className="text-gray-400 text-xs italic mt-4 mb-1 select-text">
                    ** Thanks for helping us improve. Your feedback makes your
                    agent, and others, better. While we may not reply, all
                    feedback is reviewed.
                  </StyledText>

                  {/* Footer */}
                  <View
                    className={`flex-row justify-end gap-x-3 mt-2 ${
                      error ? "mt-4" : ""
                    }`}
                  >
                    <Pressable
                      className="px-4 py-2.5 border border-gray-200 rounded-lg hover:bg-gray-100"
                      onPress={handleClose}
                    >
                      <StyledText className="text-gray-600">Cancel</StyledText>
                    </Pressable>
                    <Pressable
                      className={`min-w-[130px] px-4 py-2.5 bg-black rounded-lg hover:bg-gray-800 ${
                        isSubmitDisabled ? "opacity-50 cursor-not-allowed" : ""
                      }`}
                      onPress={handleSubmit}
                      disabled={isSubmitDisabled || isSubmitting}
                    >
                      {isSubmitting ? (
                        <ActivityIndicator size="small" color="#ffffff" />
                      ) : (
                        <StyledText className="text-white">
                          Submit Report
                        </StyledText>
                      )}
                    </Pressable>
                  </View>
                </>
              )}
            </View>
          </TouchableWithoutFeedback>
        </View>
      </TouchableWithoutFeedback>
    </Modal>
  );
};

export default memo(ReportCallModal);
